import { useAuth0 } from '@auth0/auth0-react';
import { useState } from 'react';
import { ShoppingBag, Settings, CreditCard, Heart, LogOut, Bell } from 'lucide-react';

const MemberInterface = () => {
  const { user, isLoading, logout } = useAuth0();
  const [activeTab, setActiveTab] = useState('dashboard');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-custom-red"></div>
      </div>
    );
  }

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: ShoppingBag },
    { id: 'orders', label: 'Mes commandes', icon: ShoppingBag },
    { id: 'favorites', label: 'Mes favoris', icon: Heart },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  return (
    <div className="bg-gray-50 min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* User Profile Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <img 
              src={user?.picture}
              alt={user?.name}
              className="w-16 h-16 rounded-full border-2 border-custom-red"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user?.name}</h1>
              <p className="text-gray-600">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-auto flex items-center space-x-2 text-gray-600 hover:text-custom-red transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 bg-white rounded-lg shadow-sm p-4">
            <nav className="space-y-1">
              {menuItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === id 
                      ? 'bg-custom-red text-white' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Vos Avantages Membres</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">Prix Membres</h3>
                    <p className="text-gray-600">Économisez jusqu'à 20% sur vos commandes</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">Livraison Prioritaire</h3>
                    <p className="text-gray-600">Recevez vos commandes en priorité</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-bold text-lg mb-2">Offres Exclusives</h3>
                    <p className="text-gray-600">Accédez à des offres réservées aux membres</p>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-xl font-bold mb-4">Dernières Commandes</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-600 text-center">Aucune commande récente</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div>
                <h2 className="text-xl font-bold mb-4">Historique des Commandes</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-600 text-center">Aucune commande pour le moment</p>
                </div>
              </div>
            )}

            {activeTab === 'favorites' && (
              <div>
                <h2 className="text-xl font-bold mb-4">Mes Produits Favoris</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-600 text-center">Aucun produit favori</p>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Paramètres du Compte</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-md"
                      value={user?.name || ''}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-2 border rounded-md"
                      value={user?.email || ''}
                      readOnly
                    />
                  </div>
                  <button className="bg-custom-red text-white px-6 py-2 rounded-md hover:bg-opacity-90 transition-colors">
                    Mettre à jour le profil
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberInterface; 